import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { useNavigate, useParams } from 'react-router';
import { useBackHref } from '@/hooks/useListLocation';
import { graphql } from '@/gql';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { FormField, FormLayout, FormSection } from '@/components/ui/FormLayout';

const DiscountDoc = graphql(/* GraphQL */ `
  query Discount($id: ID!) {
    discount(id: $id) {
      id
      name
      type
      isActive
      configuration
    }
    discountTypes {
      type
      label
      schema
    }
  }
`);

const DiscountTypesOnlyDoc = graphql(/* GraphQL */ `
  query DiscountTypes {
    discountTypes {
      type
      label
      schema
    }
  }
`);

const CreateDiscountDoc = graphql(/* GraphQL */ `
  mutation CreateDiscount($input: CreateDiscountInput!) {
    createDiscount(input: $input) {
      id
    }
  }
`);

const UpdateDiscountDoc = graphql(/* GraphQL */ `
  mutation UpdateDiscount($id: ID!, $input: UpdateDiscountInput!) {
    updateDiscount(id: $id, input: $input) {
      id
      name
      type
      isActive
      configuration
    }
  }
`);

interface JsonSchemaProperty {
  type?: 'string' | 'number' | 'integer' | 'boolean';
  title?: string;
  description?: string;
  enum?: Array<string | number>;
  default?: unknown;
  minimum?: number;
  maximum?: number;
  multipleOf?: number;
}

interface JsonSchema {
  type?: string;
  required?: string[];
  properties?: Record<string, JsonSchemaProperty>;
}

function parseSchema(raw: string): JsonSchema {
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed as JsonSchema;
  } catch {
    // fall through
  }
  return {};
}

function defaultForProperty(prop: JsonSchemaProperty): unknown {
  if (prop.default !== undefined) return prop.default;
  if (prop.enum && prop.enum.length > 0) return prop.enum[0];
  if (prop.type === 'boolean') return false;
  if (prop.type === 'number' || prop.type === 'integer') return '';
  return '';
}

export function DiscountEditPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  // The discounts list as it was left (search, type, page): saving and Cancel return to it.
  const backHref = useBackHref('/discounts');
  const isNew = !id || id === 'new';

  const { data: typesOnly } = useQuery(DiscountTypesOnlyDoc, { skip: !isNew });
  const { data, loading } = useQuery(DiscountDoc, {
    variables: { id: id ?? '' },
    skip: isNew,
  });

  const discountTypes = useMemo(
    () => (isNew ? typesOnly?.discountTypes : data?.discountTypes) ?? [],
    [isNew, typesOnly?.discountTypes, data?.discountTypes],
  );
  const discount = data?.discount;

  const [createDiscount, { loading: creating }] = useMutation(CreateDiscountDoc);
  const [updateDiscount, { loading: updating }] = useMutation(UpdateDiscountDoc);
  const saving = creating || updating;

  const [name, setName] = useState('');
  const [type, setType] = useState<string>('');
  const [isActive, setIsActive] = useState(true);
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [seededFor, setSeededFor] = useState<string | null>(null);

  // Re-seed from server response once it arrives.
  if (discount && discount.id !== seededFor) {
    setSeededFor(discount.id);
    setName(discount.name);
    setType(discount.type);
    setIsActive(discount.isActive);
    try {
      const parsed = JSON.parse(discount.configuration);
      if (parsed && typeof parsed === 'object') {
        setConfig(parsed as Record<string, unknown>);
      }
    } catch {
      setConfig({});
    }
  }

  const selectedType = useMemo(
    () => discountTypes.find((t) => t.type === type) ?? null,
    [discountTypes, type],
  );
  const selectedSchema = useMemo<JsonSchema>(
    () => (selectedType ? parseSchema(selectedType.schema) : {}),
    [selectedType],
  );

  const handleTypeChange = (nextType: string) => {
    setType(nextType);
    const next = discountTypes.find((t) => t.type === nextType);
    if (!next) {
      setConfig({});
      return;
    }
    const schema = parseSchema(next.schema);
    const seed: Record<string, unknown> = {};
    for (const [key, prop] of Object.entries(schema.properties ?? {})) {
      seed[key] = defaultForProperty(prop);
    }
    setConfig(seed);
  };

  // Auto-select a default type when creating.
  if (isNew && !type && discountTypes.length > 0 && !seededFor) {
    const first = discountTypes[0];
    setType(first.type);
    const schema = parseSchema(first.schema);
    const seed: Record<string, unknown> = {};
    for (const [key, prop] of Object.entries(schema.properties ?? {})) {
      seed[key] = defaultForProperty(prop);
    }
    setConfig(seed);
    setSeededFor('__new__');
  }

  const updateConfig = (key: string, value: unknown) => {
    setConfig((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saving) return;
    const fieldErrors: Record<string, string> = {};
    if (!name.trim()) fieldErrors.name = 'Name is required';
    if (!type) fieldErrors.type = 'Type is required';

    const required = selectedSchema.required ?? [];
    for (const field of required) {
      const value = config[field];
      if (value === undefined || value === null || value === '') {
        fieldErrors[`config.${field}`] = 'This field is required';
      }
    }

    const coerced: Record<string, unknown> = {};
    for (const [key, prop] of Object.entries(selectedSchema.properties ?? {})) {
      const raw = config[key];
      if (prop.type === 'number' || prop.type === 'integer') {
        if (raw === '' || raw === null || raw === undefined) {
          coerced[key] = null;
        } else {
          const num = Number(raw);
          if (Number.isNaN(num)) {
            fieldErrors[`config.${key}`] = 'Must be a number';
          } else {
            coerced[key] = num;
          }
        }
      } else if (prop.type === 'boolean') {
        coerced[key] = Boolean(raw);
      } else {
        coerced[key] = raw ?? null;
      }
    }

    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }
    setErrors({});

    const configuration = JSON.stringify(coerced);
    if (isNew) {
      await createDiscount({
        variables: { input: { name, type, configuration, isActive } },
      });
    } else if (id) {
      await updateDiscount({
        variables: {
          id,
          input: { name, type, configuration, isActive },
        },
      });
    }
    navigate(backHref);
  };

  if (!isNew && loading && !data) {
    return <p className="text-sm text-gray-500">Loading discount…</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {isNew ? 'New discount' : `Edit ${discount?.name ?? ''}`}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Pick the discount type and fill in the fields — the configuration is
          driven by the backend schema.
        </p>
      </div>

      <FormLayout
        title="Discount"
        onSubmit={handleSubmit}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              type="button"
              onClick={() => navigate(backHref)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : isNew ? 'Create discount' : 'Save discount'}
            </Button>
          </div>
        }
      >
        <FormSection title="Basics">
          <FormField label="Name" required error={errors.name}>
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              invalid={Boolean(errors.name)}
              required
            />
          </FormField>
          <FormField label="Type" required error={errors.type}>
            <Select
              value={type}
              onChange={(event) => handleTypeChange(event.target.value)}
              options={discountTypes.map((t) => ({
                value: t.type,
                label: t.label,
              }))}
              invalid={Boolean(errors.type)}
            />
          </FormField>
          <FormField label="Active">
            <Switch
              checked={isActive}
              onChange={(event) => setIsActive(event.target.checked)}
              label={isActive ? 'Applied at checkout' : 'Disabled'}
            />
          </FormField>
        </FormSection>

        {selectedSchema.properties &&
          Object.keys(selectedSchema.properties).length > 0 && (
            <FormSection
              title="Configuration"
              description="Schema-driven fields provided by the selected discount type."
            >
              {Object.entries(selectedSchema.properties).map(([key, prop]) => (
                <DynamicField
                  key={key}
                  name={key}
                  schema={prop}
                  required={(selectedSchema.required ?? []).includes(key)}
                  value={config[key]}
                  onChange={(value) => updateConfig(key, value)}
                  error={errors[`config.${key}`]}
                />
              ))}
            </FormSection>
          )}
      </FormLayout>
    </div>
  );
}

function DynamicField({
  name,
  schema,
  required,
  value,
  onChange,
  error,
}: {
  name: string;
  schema: JsonSchemaProperty;
  required: boolean;
  value: unknown;
  onChange: (next: unknown) => void;
  error?: string;
}) {
  const label = schema.title ?? name;

  if (schema.enum && schema.enum.length > 0) {
    return (
      <FormField
        label={label}
        required={required}
        description={schema.description}
        error={error}
      >
        <Select
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(event) => onChange(event.target.value)}
          options={schema.enum.map((v) => ({
            value: String(v),
            label: String(v),
          }))}
          invalid={Boolean(error)}
        />
      </FormField>
    );
  }

  if (schema.type === 'boolean') {
    return (
      <FormField
        label={label}
        description={schema.description}
        error={error}
      >
        <Switch
          checked={Boolean(value)}
          onChange={(event) => onChange(event.target.checked)}
          label={value ? 'Enabled' : 'Disabled'}
        />
      </FormField>
    );
  }

  if (schema.type === 'number' || schema.type === 'integer') {
    return (
      <FormField
        label={label}
        required={required}
        description={schema.description}
        error={error}
      >
        <Input
          type="number"
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(event) => onChange(event.target.value)}
          min={schema.minimum}
          max={schema.maximum}
          step={schema.multipleOf ?? (schema.type === 'integer' ? 1 : 'any')}
          invalid={Boolean(error)}
          required={required}
        />
      </FormField>
    );
  }

  return (
    <FormField
      label={label}
      required={required}
      description={schema.description}
      error={error}
    >
      <Input
        type="text"
        value={value === undefined || value === null ? '' : String(value)}
        onChange={(event) => onChange(event.target.value)}
        invalid={Boolean(error)}
        required={required}
      />
    </FormField>
  );
}
